import { motion } from "framer-motion";

const CHAPTERS = [
  {
    n: "I",
    title: "We take fewer briefs on purpose.",
    body:
      "Six to eight engagements a year. That number isn't false scarcity; it's the ceiling of what two hands can do properly. If you can't get on the calendar, we're not sandbagging. We're sold out.",
  },
  {
    n: "II",
    title: "Strategy is a drawing tool.",
    body:
      "We refuse the false split between strategy and design. Every recommendation we make has to be renderable. Every mark we draw has to defend a claim. If the two disagree, one of them is wrong.",
  },
  {
    n: "III",
    title: "The brief is the product.",
    body:
      "Half of a Bosecrafts engagement is a document nobody but you and your board will ever read. That document — the brief — is where the money is really made or lost. We spend accordingly.",
  },
  {
    n: "IV",
    title: "Founder-facing, always.",
    body:
      "We don't run through account managers. You'll talk to the person doing the thinking and the person doing the drawing — often, they're the same person, over a slow coffee.",
  },
  {
    n: "V",
    title: "Boring is a compliment.",
    body:
      "The brands we admire don't look loud in an Instagram grid. They look inevitable in a board room. If our work looks 'quiet', that's the intended tone of voice — not an absence of one.",
  },
];

export default function Studio() {
  return (
    <div className="pt-40 md:pt-56 pb-24" data-testid="studio-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">[ Vol. III ] · Studio</p>
        <h1 className="mt-8 font-display text-6xl md:text-[11rem] leading-[0.86] tracking-tightest">
          A very <em className="italic text-stone-muted">small</em>
          <br />
          studio.
        </h1>

        <div className="mt-16 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7">
            <p className="font-serif text-xl md:text-2xl leading-relaxed text-charcoal/80">
              Bosecrafts is a two-person brand consultancy based in Siliguri, India, working
              internationally with founders at the intersection of strategy and design. We think about
              brands the way editors think about books — voice first, cover second.
            </p>
            <p className="mt-8 font-serif text-lg leading-relaxed text-charcoal/70">
              Our clients are founders who already sell something well and want to sell it a great deal
              better. We are less useful before product-market fit, and more useful after.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="frame spotlight aspect-[3/4]">
              <img
                src="https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Studio interior"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Manifesto */}
      <section className="mt-40 bg-ink text-ivory py-24 md:py-40" data-testid="manifesto">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-ivory/60">[ 02 ] · Manifesto</p>
          <h2 className="mt-8 font-display text-5xl md:text-8xl tracking-tightest leading-none">
            Five things we <em className="italic text-ivory/60">believe</em>.
          </h2>

          <div className="mt-24 space-y-24">
            {CHAPTERS.map((ch, i) => (
              <motion.div
                key={ch.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
                className="grid grid-cols-12 gap-8 border-t border-ivory/20 pt-10"
              >
                <div className="col-span-12 md:col-span-3">
                  <span className="numeral text-7xl md:text-9xl text-ivory/40">{ch.n}</span>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-4xl md:text-6xl tracking-tightest leading-tight">
                    {ch.title}
                  </h3>
                  <p className="mt-6 max-w-xl text-ivory/75 leading-relaxed text-lg">{ch.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24 md:py-40 border-b border-charcoal/15">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-5">
            <div className="frame spotlight aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/32825614/pexels-photo-32825614.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Founder"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">[ 03 ] · Founder</p>
            <h2 className="mt-6 font-display text-5xl md:text-7xl tracking-tightest leading-none">
              Arka Bose,
              <br />
              <em className="italic text-stone-muted">principal.</em>
            </h2>
            <p className="mt-10 font-serif text-lg leading-relaxed text-charcoal/70">
              Fifteen years of building brands, twelve of them for founders. Formerly of two agencies I
              won't name and one boutique I'd rather. Trained as a printmaker, retrained by the market.
            </p>
            <p className="mt-6 font-serif text-lg leading-relaxed text-charcoal/70">
              I run every engagement personally. If you email us, I read it. If we take the project, I
              lead it. There is no bench.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
